import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react'
import { authApi } from '../services/api'
import { BrandWordmark } from '../components/brand/BrandMark'

/**
 * Password-reset completion page — self-service "forgot password" flow.
 *
 * Lands here from the reset email (/reset-password?token=…). We ask for
 * a new password (+ confirmation), POST /api/auth/reset-password, and on
 * success bounce the user to /login. Unlike the /activate page we do NOT
 * auto-login: the server intentionally issues no JWT, so the user signs
 * in fresh with their new password (proves they know it). An invalid /
 * expired token surfaces a generic error with a path back to request a
 * new link — the server never tells us which check failed.
 */
export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') || ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  // On success, send the user to the login form after a short beat so
  // they can read the confirmation. The button below is the manual path.
  useEffect(() => {
    if (!done) return
    const t = setTimeout(() => navigate('/login'), 2500)
    return () => clearTimeout(t)
  }, [done, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }
    setSubmitting(true)
    try {
      await authApi.resetPassword(token, password)
      setDone(true)
    } catch (e: any) {
      setError(e.response?.data?.error || 'Reset failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 shadow-lg">
        {/* White label §4.1 — the password-reset page is a branded surface. */}
        <BrandWordmark className="mb-6" />

        {!token ? (
          <div>
            <h1 className="text-xl font-bold text-foreground mb-2">Reset link is invalid</h1>
            <p className="text-sm text-muted-foreground">
              This password-reset link is missing its token. Request a fresh one from the
              login page.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="mt-4 px-4 py-2 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition"
            >
              Go to login
            </button>
          </div>
        ) : done ? (
          <div className="text-center">
            <CheckCircle2 size={40} className="mx-auto text-primary mb-3" />
            <h1 className="text-xl font-bold text-foreground mb-2">Password updated</h1>
            <p className="text-sm text-muted-foreground">
              Your password has been reset. Redirecting you to the login page…
            </p>
            <button
              onClick={() => navigate('/login')}
              className="mt-4 px-4 py-2 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition"
            >
              Go to login
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h1 className="text-xl font-bold text-foreground">Set a new password</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Choose a new password for your Rx2Go account.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">
                  New password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    minLength={6}
                    autoFocus
                    required
                    className="w-full bg-card text-foreground rounded px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">
                  Confirm password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  minLength={6}
                  required
                  className="w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                />
              </div>
              {error && (
                <div>
                  <p className="text-destructive text-sm">{error}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    The link may have expired.{' '}
                    <Link to="/login" className="text-primary hover:underline">
                      Request a new reset link
                    </Link>
                    .
                  </p>
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2.5 rounded transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                Reset password
              </button>
            </form>
            <p className="mt-4 text-sm text-muted-foreground text-center">
              <Link to="/login" className="text-primary hover:underline">
                Back to login
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
